import { z } from "zod";

// ============================================================================
// MESSENGER PAGES (Facebook Page Sender Discovery)
// ============================================================================

/**
 * List Messenger Pages Query Schema
 *
 * Query parameters for listing Facebook Messenger page senders
 * from the Twilio Channels API via a parent Twilio connection.
 */
export const ListMessengerPagesQuerySchema = z.object({
  /** Parent Twilio connection ID (ApiKeyConnection with service=TWILIO) */
  connectionId: z.string().uuid()
});

export type ListMessengerPagesQuery = z.infer<typeof ListMessengerPagesQuerySchema>;

/**
 * Messenger Page Schema
 *
 * Represents a Facebook Page discovered from the Twilio Channels API,
 * enriched with our DB link status (child connection + deployment).
 */
export const MessengerPageSchema = z.object({
  /** Facebook Page ID (numeric string, e.g. "103657271176843") */
  facebookPageId: z.string(),
  /** Human-readable page name from Twilio/Meta profile */
  pageName: z.string(),
  /** Sender status from Twilio (e.g. "ONLINE", "OFFLINE") */
  status: z.string(),
  /** Child FB_MESSENGER_PAGE connection ID if already linked, null otherwise */
  connectionId: z.string().uuid().nullable(),
  /** Deployment ID if the child connection is bound to an active deployment, null otherwise */
  deploymentId: z.string().uuid().nullable()
});

export type MessengerPage = z.infer<typeof MessengerPageSchema>;

/**
 * List Messenger Pages Response Schema
 */
export const ListMessengerPagesResponseSchema = z.array(MessengerPageSchema);

export type ListMessengerPagesResponse = z.infer<typeof ListMessengerPagesResponseSchema>;

// ============================================================================
// FACEBOOK TOKEN EXCHANGE (Direct Meta OAuth — step 1)
// ============================================================================

/**
 * Exchange Facebook Token Body Schema
 *
 * Frontend sends the short-lived user access token from FB.login. Backend
 * exchanges it for a long-lived token, caches page tokens server-side in a
 * ProviderHandshake, and returns page info WITHOUT tokens.
 */
export const ExchangeFacebookTokenBodySchema = z.object({
  /** Short-lived user access token from FB.login */
  userAccessToken: z.string().min(1)
});

export type ExchangeFacebookTokenBody = z.infer<typeof ExchangeFacebookTokenBodySchema>;

/**
 * Facebook Page info (safe for frontend — NO access tokens).
 */
export const FacebookPageSafeSchema = z.object({
  /** Facebook Page ID (numeric string) */
  id: z.string(),
  /** Human-readable page name */
  name: z.string(),
  /** Page category (e.g. "Software", "Brand") */
  category: z.string()
});

export type FacebookPageSafe = z.infer<typeof FacebookPageSafeSchema>;

/**
 * Exchange Facebook Token Response Schema
 *
 * Returns a flowId (used to connect a page in step 2) and the pages the user
 * manages WITHOUT access tokens. Tokens stay server-side in the handshake.
 */
export const ExchangeFacebookTokenResponseSchema = z.object({
  /** Temporary flow ID — use with ConnectFacebookPage to complete setup */
  flowId: z.string(),
  /** Pages the user manages (no tokens — safe for browser) */
  pages: z.array(FacebookPageSafeSchema)
});

export type ExchangeFacebookTokenResponse = z.infer<typeof ExchangeFacebookTokenResponseSchema>;

// ============================================================================
// CONNECT FACEBOOK PAGE (step 2 — server-side connection creation)
// ============================================================================

/**
 * Connect Facebook Page Body Schema
 *
 * After the user picks a page from the exchange-token response, frontend sends
 * the flowId + pageId. Backend retrieves the cached Page Access Token, creates
 * the connection, and subscribes the webhook. Tokens never reach the browser.
 */
export const ConnectFacebookPageBodySchema = z.object({
  /** Flow ID from ExchangeFacebookToken response */
  flowId: z.string().min(1),
  /** Facebook Page ID the user selected */
  pageId: z.string().min(1)
});

export type ConnectFacebookPageBody = z.infer<typeof ConnectFacebookPageBodySchema>;

/**
 * Connect Facebook Page Response Schema
 */
export const ConnectFacebookPageResponseSchema = z.object({
  /** Created ApiKeyConnection ID */
  connectionId: z.string().uuid(),
  /** Page name (for display confirmation) */
  pageName: z.string()
});

export type ConnectFacebookPageResponse = z.infer<typeof ConnectFacebookPageResponseSchema>;
